import { Component, computed, inject, PLATFORM_ID } from '@angular/core';
import WertWidget from '@wert-io/widget-initializer';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { v4 as uuidv4 } from 'uuid';

import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { selectedLang } from '../../app.component';
import { AnalyticsService } from '../../services/analytics.service';
import { PresaleDataService } from '../../services/connection-services/data.service';
import { EvmPurchseService } from '../../services/evm-services/evm-purchase.service';
import { SolPurchaseService } from '../../services/sol-services/sol-purchase.service';
import { CHAINS, SwapStatus, wertLangs } from '../../shared/constants/app-enums';
import { getABI } from '../../shared/constants/wallet-connect.const';
import { IWertPayload } from '../../shared/interfaces/common.interface';
import { PixelService } from '../../services/pixel.service';

import solWalletsJson from '../../../assets/json/SOL_WALLETS.json';
// Cast to Record so wallet IDs (dynamic strings) can be used as index keys
const solWallets = solWalletsJson as Record<string, string>;

@Component({
    selector: 'app-wert',
    templateUrl: './wert.component.html',
    styleUrls: ['./wert.component.scss'],
    standalone: false
})
export class WertComponent {
  token: string = environment.tokenName;
  baseToken!: number;
  swapToken!: number;
  isStake = false;
  walletAddress!: string;
  wertWidget!: WertWidget;
  wertOptions: {
    partner_id: string;
    origin: string;
    click_id: string; // must be unique for any request
    lang: string;
    theme: 'dark' | undefined;
    currency: string;
    commodity: string;
    network: string;
    redirect_url: string;
    extra: {
      item_info: {
        name: string;
        image_url: string;
      };
    };
  } = {
      partner_id: environment.partnerId,
      origin: environment.wertOrigin,
      click_id: uuidv4(), // must be unique for any request
      lang: wertLangs[selectedLang] || 'en',
      theme: 'dark',
      currency: 'USD',
      commodity: 'USDC',
      network: environment.production ? 'ethereum' : 'sepolia',
      redirect_url: environment.domain,
      extra: {
        item_info: {
          name: environment.tokenName,
          image_url: environment.domain + '/assets/images/svg-icons/logo.svg',
        },
      },
    };
  SwapStatus = SwapStatus;
  mountError: boolean = false;
  currentStep: number = 0;
  walletType: string = '';
  txId: string = '';

  private readonly _platformId = inject(PLATFORM_ID);
  readonly modalRef = inject(BsModalRef);
  private readonly http = inject(HttpClient);
  readonly dataService = inject(PresaleDataService);
  readonly evmPurchaseService = inject(EvmPurchseService);
  private readonly solPurchaseService = inject(SolPurchaseService);
  private readonly pixelService = inject(PixelService);
  private readonly analyticsService = inject(AnalyticsService);

  readonly swapStatus = computed(() => this.evmPurchaseService.transactionUpdates().status);

  /**
   * Get Wert Payload according to the selected chain
   * @returns - Custom payload for both Eth and Sol chains
   */
  async getWertPayload(): Promise<IWertPayload> {
    let commodityAmount = 0;
    let inputData = '';
    if (this.dataService.activeChain === CHAINS.SOL) {
      const amount = +(this.baseToken * this.dataService.balanceDataSOl.oneSol);
      commodityAmount = +((amount * 1.02) + 0.0015).toFixed(6);
      try {
        inputData = await this.solPurchaseService.buyWithWert(this.baseToken, +((amount * 1.02)).toFixed(6));
      } catch {
        // payload build failed — mountError shown to user
        this.mountError = true;
      }
    } else {
      commodityAmount = +this.swapToken;
      inputData = await getABI(this.baseToken, this.isStake, this.walletAddress); // this.dataService.chainId, this.dataService.wagmiConfig
    }
    return {
      address: this.walletAddress,
      commodity: this.dataService.activeChain === CHAINS.SOL ? 'SOL' : 'USDC',
      network: this.dataService.activeChain === CHAINS.SOL ? 'solana' : environment.production ? 'ethereum' : 'sepolia',
      commodity_amount: commodityAmount,
      sc_address: this.dataService.activeChain === CHAINS.SOL ? environment.sol.presaleAddress : environment.eth.presaleAddress,
      sc_input_data: inputData,
    };
  }

  /**
   * Creates Wert options object according to selected chain
   * @returns - Wert Options according to the chain selected
   */
  getWertOptions() {
    return {
      partner_id: environment.partnerId,
      origin: environment.wertOrigin,
      click_id: uuidv4(), // must be unique for any request
      lang: wertLangs[selectedLang] || 'en',
      theme: 'dark' as const,
      currency: 'USD',
      commodity: this.dataService.activeChain === CHAINS.SOL ? 'SOL' : 'USDC',
      network: this.dataService.activeChain === CHAINS.SOL ? 'solana' : environment.production ? 'ethereum' : 'sepolia',
      redirect_url: environment.domain,
      extra: {
        item_info: {
          name: environment.tokenName,
          image_url: environment.domain + '/assets/images/svg-icons/logo.svg',
        },
      },
    }
  }

  async showWidget() {
    this.currentStep = 1;
    const payload = await this.getWertPayload();
    const wertOptions = this.getWertOptions();


    if (isPlatformBrowser(this._platformId)) {
      if (this.dataService.activeChain === CHAINS.EVM) {
        this.walletType = localStorage.getItem('@appkit/eip155:connected_connector_id') as string;
      } else {
        const walletID = localStorage.getItem('@appkit/solana:connected_connector_id') as string;
        this.walletType = solWallets[walletID] ?? walletID;
      }
      // Response shape is defined by the Wert signing endpoint — `data` contains signed widget params
      this.http.post<{ data: Record<string, unknown> }>(environment.wertUrl, payload).subscribe({
        next: (res) => {
          const signedData = res.data;

          this.wertWidget = new WertWidget({
            ...signedData,
            ...wertOptions,
            listeners: {
              loaded: () => {
                this.evmPurchaseService.setTransactionStatus(SwapStatus.confirm_pending);
                this.modalRef.hide();
                this.analyticsService.vwoData('wert', { openWert: true, wertPaymentStatus: 'initiated-wert', walletAddress: this.walletAddress, wertTransactionId: '' });
                this.txId = this.pixelService.generateTransactionId();
                this.pixelService.sendPixelEvent('buy_event', {
                  action: 'initiated',
                  tx_id: this.txId,
                  wallet_address: this.walletAddress,
                  wallet_type: this.dataService.activeChain === CHAINS.EVM ? 'EVM' : 'SOL',
                  wallet_name: this.walletType || this.dataService.connectedWalletName,
                  token_symbol: environment.tokenName,
                  chain: this.dataService.activeChain === CHAINS.EVM ? 'ETH' : 'SOL',
                  chain_id: this.dataService.activeChain === CHAINS.EVM ? environment.eth.chainIdInt : environment.sol.chainIdInt,
                  amount_token_expected: this.baseToken,
                  amount_usd_estimated: this.swapToken,
                  payment_method: 'card',
                  type: this.isStake ? 'buy_and_stake' : 'buy'
                });
              },
              close: () => {
                if (this.swapStatus() !== SwapStatus.wert_inprogress && this.swapStatus() !== SwapStatus.complete) this.evmPurchaseService.setTransactionStatus(SwapStatus.rejected);
              },
              position: (_data: { step: string }) => {},
              'payment-status': (status: { status: string; payment_id: string; order_id: string; tx_id: string }) => {
                if (status.status == 'progress' || status.status == 'pending') {
                  this.evmPurchaseService.setTransactionStatus(SwapStatus.in_progess);
                }

                if (status.status == 'success' && status.tx_id) {
                  this.wertWidget.removeEventListeners();
                  this.wertWidget.close();
                  this.analyticsService.vwoData('wert', { openWert: false, wertPaymentStatus: 'success', walletAddress: this.walletAddress, wertTransactionId: status.tx_id });

                  if (this.dataService.activeChain === CHAINS.EVM) {
                    this.evmPurchaseService.checkTransaction(status.tx_id, 'usd', this.baseToken, this.swapToken, "buy", "usd", this.txId); // checking transaction status
                  } else {
                    this.evmPurchaseService.setTransactionStatus(SwapStatus.complete, status.tx_id);
                    this.analyticsService.vwoData('successfulTransaction', {
                      walletAddress: this.dataService.walletAddress,
                      transactionId: status.tx_id,
                      mode: 'WERT',
                      walletName: this.dataService.connectedWalletName,
                      purchaseType: "buy",
                      tokenAmountPurchase: this.baseToken,
                      amountSpent: this.swapToken,
                      cryptoType: this.dataService.activeChain === CHAINS.SOL ? "sol" : "USDC",
                      usdAmount: +(+(this.baseToken * this.dataService.tokenomics[this.dataService.balanceDataEVM.currentStep].tokenUSDT).toFixed(2)),
                    });
                  }
                  this.pixelService.sendPixelEvent('buy_event', {
                    action: 'pending',
                    tx_id: this.txId,
                    wallet_address: this.walletAddress,
                    wallet_type: this.dataService.activeChain === CHAINS.EVM ? 'EVM' : 'SOL',
                    wallet_name: this.walletType || this.dataService.connectedWalletName,
                    token_symbol: environment.tokenName,
                    chain: this.dataService.activeChain === CHAINS.EVM ? 'ETH' : 'SOL',
                    chain_id: this.dataService.activeChain === CHAINS.EVM ? environment.eth.chainIdInt : environment.sol.chainIdInt,
                    amount_token_expected: this.baseToken,
                    amount_usd_estimated: this.swapToken,
                    payment_method: 'card',
                    tx_hash: status.tx_id,
                    type: this.isStake ? 'buy_and_stake' : 'buy'
                  });
                }

                if (status.status == 'canceled') {
                  this.evmPurchaseService.setTransactionStatus(SwapStatus.rejected);
                  this.analyticsService.vwoData('wert', { openWert: false, wertPaymentStatus: 'rejected', walletAddress: this.walletAddress, wertTransactionId: status.tx_id || '' });
                  this.analyticsService.vwoData('failedAndRejectTransaction', {
                    transactionStatus: 'rejected',
                    fundSpentAmount: this.swapToken,
                    tokenAmountPurchase: this.baseToken,
                    purchaseType: this.isStake ? 'buy_and_stake' : 'buy',
                    tokenPurchaseMethod: 'card',
                    transactionId: status.tx_id,
                    walletAddress: this.walletAddress,
                    walletType: this.walletType,
                  });
                  // Track buy failed (user canceled)
                  this.pixelService.sendPixelEvent('buy_event', {
                    action: 'failed',
                    tx_id: this.txId,
                    wallet_address: this.walletAddress,
                    wallet_type: this.dataService.activeChain === CHAINS.EVM ? 'EVM' : 'SOL',
                    wallet_name: this.walletType || this.dataService.connectedWalletName,
                    token_symbol: environment.tokenName,
                    chain: this.dataService.activeChain === CHAINS.EVM ? 'ETH' : 'SOL',
                    chain_id: this.dataService.activeChain === CHAINS.EVM ? environment.eth.chainIdInt : environment.sol.chainIdInt,
                    payment_method: 'card',
                    failure_type: 'user_rejected',
                    failure_message: 'User canceled Wert payment',
                    type: this.isStake ? 'buy_and_stake' : 'buy'
                  });
                }
              },
              // Wert error event data shape: { name: string; message: string }
              error: (_err: { name: string; message: string }) => {
                this.evmPurchaseService.setTransactionStatus(SwapStatus.failed);
                this.analyticsService.vwoData('wert', { openWert: false, wertPaymentStatus: 'failed', walletAddress: this.walletAddress, wertTransactionId: '' });
                this.analyticsService.vwoData('failedAndRejectTransaction', {
                  transactionStatus: 'failed',
                  fundSpentAmount: this.swapToken,
                  tokenAmountPurchase: this.baseToken,
                  purchaseType: this.isStake ? 'buy_and_stake' : 'buy',
                  tokenPurchaseMethod: 'card',
                  transactionId: '',
                  walletAddress: this.walletAddress,
                  walletType: this.walletType,
                });
                // Track buy failed (Wert error)
                this.pixelService.sendPixelEvent('buy_event', {
                  action: 'failed',
                  tx_id: this.txId,
                  wallet_address: this.walletAddress,
                  wallet_type: this.dataService.activeChain === CHAINS.EVM ? 'EVM' : 'SOL',
                  wallet_name: this.walletType || this.dataService.connectedWalletName,
                  token_symbol: environment.tokenName,
                  chain: this.dataService.activeChain === CHAINS.EVM ? 'ETH' : 'SOL',
                  chain_id: this.dataService.activeChain === CHAINS.EVM ? environment.eth.chainIdInt : environment.sol.chainIdInt,
                  payment_method: 'card',
                  failure_type: 'wert_error',
                  failure_message: 'Wert payment failed',
                  type: this.isStake ? 'buy_and_stake' : 'buy'
                });
              },
            },
          });
          this.wertWidget.open();
        },
        error: (_err: any) => {
          this.mountError = true;
          this.evmPurchaseService.resetTransactionStatus();
        },
      });
    }
  }
}
